import React, { useState, useMemo } from "react";
import { User, Phone, MapPin, Receipt, Search, X } from "lucide-react";
import { formatJMD, STATUS_META, fmtDate } from "@/lib/format";
import { matchesQuery } from "@/pages/admin/OrdersTab";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const lastOrder = (c) => c.orders.reduce((m, o) => (o.created_at > m ? o.created_at : m), "");

const SORTS = {
  recent: { label: "Most recent order", fn: (a, b) => lastOrder(b).localeCompare(lastOrder(a)) },
  name: { label: "Name A–Z", fn: (a, b) => a.name.localeCompare(b.name) },
  spend: { label: "Lifetime spend", fn: (a, b) => b.lifetime_spend - a.lifetime_spend },
  orders: { label: "Most orders", fn: (a, b) => b.total_orders - a.total_orders },
};

export const CustomersTab = ({ customers }) => {
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("recent");

  const shown = useMemo(() => customers
    .filter((c) => matchesQuery(query, c.name, c.phone, c.address))
    .sort(SORTS[sort].fn), [customers, query, sort]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="admin-customers-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, phone or address…" className="h-10 w-72 pl-9 rounded-xl" />
          {query && <button onClick={() => setQuery("")} data-testid="admin-customers-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="admin-customers-sort" className="w-48 h-10 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-slate-500 ml-auto" data-testid="admin-customers-count">{shown.length} of {customers.length} customers</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-4" data-testid="admin-customers-list">
        {shown.map((c) => (
          <div key={c.phone} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`customer-card-${c.phone}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3 min-w-0">
                <div className="w-11 h-11 shrink-0 rounded-2xl cw-ocean-gradient flex items-center justify-center">
                  <User className="w-5 h-5 text-sky-200" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-slate-900 truncate">{c.name}</div>
                  <div className="text-sm text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</div>
                  <div className="text-sm text-slate-400 flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" />{c.address}</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono-cw text-lg font-extrabold text-sky-700">{formatJMD(c.lifetime_spend)}</div>
                <div className="text-xs text-slate-400">{c.total_orders} orders</div>
              </div>
            </div>

            <button
              onClick={() => setOpenId(openId === c.phone ? null : c.phone)}
              data-testid={`customer-history-toggle-${c.phone}`}
              className="mt-4 text-xs font-semibold text-sky-600 hover:text-sky-700 flex items-center gap-1"
            >
              <Receipt className="w-3.5 h-3.5" /> {openId === c.phone ? "Hide" : "View"} order history
            </button>

            {openId === c.phone && (
              <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                {c.orders.map((o) => (
                  <div key={o.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-mono-cw text-sky-700">{o.order_code}</span>
                      <Badge className={`border text-[10px] ${STATUS_META[o.status]?.cls}`}>{STATUS_META[o.status]?.label}</Badge>
                    </div>
                    <div className="text-slate-500">{fmtDate(o.delivery_date)}</div>
                    <div className="font-mono-cw font-semibold text-slate-800">{formatJMD(o.total_amount)}</div>
                  </div>
                ))}
                {c.orders.length === 0 && <div className="text-xs text-slate-400">No orders yet.</div>}
              </div>
            )}
          </div>
        ))}
        {shown.length === 0 && (
          <div className="col-span-full bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-400">
            {query ? `No customers match "${query}".` : "No customers yet."}
          </div>
        )}
      </div>
    </div>
  );
};
